import { useEffect, useRef } from 'react'
import { buildGoogleFontsUrl, getFontByFamily } from '../data/googleFonts'

const LINK_ID_PREFIX = 'google-fonts-'

// Track loaded fonts to avoid duplicate requests
const loadedFonts = new Set<string>()

export function useFontLoader(families: string[]) {
  const previousFamiliesRef = useRef<string[]>([])

  useEffect(() => {
    // Filter to fonts that need loading (not System UI and not already loaded)
    const fontsToLoad = families.filter((family) => {
      if (family === 'System UI') return false
      if (loadedFonts.has(family)) return false
      const font = getFontByFamily(family)
      return font && font.googleName !== ''
    })

    if (fontsToLoad.length === 0) return

    // Check if these fonts are already loading/loaded
    const newFonts = fontsToLoad.filter((f) => !loadedFonts.has(f))
    if (newFonts.length === 0) return

    // Build URL for new fonts
    const url = buildGoogleFontsUrl(newFonts)
    if (!url) return

    // Create a unique ID for this link element
    const linkId = `${LINK_ID_PREFIX}${newFonts.join('-').replace(/\s+/g, '-')}`

    // Check if link already exists
    if (document.getElementById(linkId)) {
      newFonts.forEach((f) => loadedFonts.add(f))
      return
    }

    // Create and inject the link element
    const link = document.createElement('link')
    link.id = linkId
    link.rel = 'stylesheet'
    link.href = url

    link.onload = () => {
      newFonts.forEach((f) => loadedFonts.add(f))
    }

    document.head.appendChild(link)

    // Track that we're loading these fonts
    newFonts.forEach((f) => loadedFonts.add(f))

    previousFamiliesRef.current = families
  }, [families])
}

// Preload specific fonts before they're used
export function preloadFonts(families: string[]): Promise<void> {
  return new Promise((resolve) => {
    const fontsToLoad = families.filter((family) => {
      if (family === 'System UI') return false
      if (loadedFonts.has(family)) return false
      const font = getFontByFamily(family)
      return font && font.googleName !== ''
    })

    if (fontsToLoad.length === 0) {
      resolve()
      return
    }

    const url = buildGoogleFontsUrl(fontsToLoad)
    if (!url) {
      resolve()
      return
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url

    link.onload = () => {
      fontsToLoad.forEach((f) => loadedFonts.add(f))
      resolve()
    }

    link.onerror = () => {
      // Still resolve on error to not block the app
      resolve()
    }

    document.head.appendChild(link)
  })
}

// Check if a font is loaded
export function isFontLoaded(family: string): boolean {
  if (family === 'System UI') return true
  return loadedFonts.has(family)
}
